/**
 * The six time windows, and only those six.
 *
 * They are not arbitrary: they are the presets the Cloudflare dashboard itself
 * offers, and the backend caches exactly these (`app.services.metrics.Window`).
 * A free-form range picker would look friendlier and be a lie — an arbitrary
 * range would miss the cache, fire a live GraphQL query per keystroke, and hit
 * the account's query budget.
 */
import { WINDOW_IDS, WINDOW_LABEL_KEYS } from '../api/types'
import type { WindowId } from '../api/types'
import { useI18n } from '../i18n'

export interface WindowPickerProps {
  value: WindowId
  onChange: (next: WindowId) => void
  disabled?: boolean
}

export function WindowPicker({ value, onChange, disabled = false }: WindowPickerProps) {
  const { t } = useI18n()

  return (
    <div
      role="radiogroup"
      aria-label={t('dashboard.windowPicker.label')}
      className="inline-flex items-center rounded-md border border-border bg-surface p-0.5"
    >
      {WINDOW_IDS.map((id) => {
        const active = id === value
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={t(WINDOW_LABEL_KEYS[id])}
            disabled={disabled}
            // Re-selecting the current window would invalidate the query and
            // re-render 1,062 rows for an identical answer.
            onClick={() => !active && onChange(id)}
            className={[
              'num rounded px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60',
              active ? 'bg-accent text-white' : 'text-muted hover:text-fg',
            ].join(' ')}
          >
            {id}
          </button>
        )
      })}
    </div>
  )
}
